import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react"
import { Dropdown } from "components/ui/dropdown/Dropdown"
import { ButtonBurger } from "components/ui/buttons/ButtonBurger/ButtonBurger"
import { CloseIcon } from "components/ui/icons/close/CloseIcon"
import { ControllICon } from "components/ui/icons/quotations/viewer/ControlIcon"
import {
	CheckmarkIcon,
	EllipseShapeIcon,
	FitToPageIcon,
	FitToWidthIcon,
	HighlightToolIcon,
	NoteToolIcon,
	OpacityIcon,
	PanToolIcon,
	RectangleShapeIcon,
	SelectToolIcon,
	ShapesToolIcon,
	StrokeWidthIcon,
} from "components/ui/icons/quotations/viewer/CanvasToolIcons"
import { RulerIcon } from "components/ui/icons/quotations/viewer/RulerIcon"
import { ColorSwatchPicker } from "../shared/ColorSwatchPicker/ColorSwatchPicker"
import { FontSizePicker } from "../shared/FontSizePicker/FontSizePicker"
import { NOTE_TEXT_COLOR_PALETTE, NOTE_FONT_SIZE_OPTIONS } from "../interactions/note.constants"
import { useNoteToolStore } from "store/noteTool.store"
import { SliderPopover } from "../shared/SliderPopover/SliderPopover"
import { LineStylePicker } from "../shared/LineStylePicker/LineStylePicker"
import {
	CLOUDY_LINE_STYLE,
	MAX_HIGHLIGHT_OPACITY,
	MAX_HIGHLIGHT_STROKE_WIDTH,
	MIN_HIGHLIGHT_OPACITY,
	MIN_HIGHLIGHT_STROKE_WIDTH,
} from "../interactions/figureHighlight.constants"
import {
	MAX_FREEHAND_HIGHLIGHT_STROKE_WIDTH,
	MIN_FREEHAND_HIGHLIGHT_STROKE_WIDTH,
} from "../interactions/freehandHighlight.constants"
import AnnotationService from "services/annotations.service"
import { useFigureHighlightStore } from "store/figureHighlight.store"
import type { TFigureShapeType } from "store/figureHighlight.store"
import { EcallIcon } from "components/ui/icons/quotations/viewer/EcallIcon"
import { ErefStatusIcon } from "components/ui/customWebviewer/ElevationReferencesSidebar/icons/ErefStatusIcon"
import { FurnitureStatusIcon } from "components/ui/customWebviewer/ElevationReferencesSidebar/icons/FurnitureStatusIcon"
import { HelpIcon } from "components/ui/icons/quotations/viewer/HelpIcon"
import { SettingsIcon } from "components/ui/icons/quotations/viewer/SettingsIcon"
import {
	SplitViewIcon,
	CloseLeftViewIcon,
	CloseRightViewIcon,
	AutoOpenFloorPlanIcon,
	PopOutViewIcon,
	ReturnViewBackIcon,
} from "components/ui/icons/quotations/viewer/SplitViewIcons"
import { LayoutSinglePageIcon, LayoutFacingIcon } from "components/ui/icons/quotations/viewer/LayoutModeIcons"
import { CheckIcon } from "components/ui/customWebviewer/SelectionToolbar/icons"
import { RESULT_CSV_MESSAGES } from "helpers/resultCsv.constants"
import { RESULT_PDF_MESSAGES } from "helpers/resultPdf.constants"
import { DETAILS_WORD_MESSAGES } from "helpers/detailsWord.constants"
import { useSplitScreenStore } from "store/splitScreen.store"
import { usePopOutWindowStore } from "store/popOutWindow.store"
import { isPopOutWindowRoute } from "components/ui/customWebviewer/core/popOutWindow.helpers"
import { useViewerMenusStore } from "store/viewerMenus.store"
import type { TViewerMenuItem } from "store/viewerMenus.store"
import chevronDownIcon from "assets/icons/quotation-viewer/chevron-down.svg"
import circlePlusIcon from "assets/icons/quotation-viewer/circle-plus.svg"
import circleMinusIcon from "assets/icons/quotation-viewer/circle-minus.svg"
import zoomInIcon from "assets/icons/quotation-viewer/zoom-in.svg"
import zoomOutIcon from "assets/icons/quotation-viewer/zoom-out.svg"
import { Tooltip } from "antd"
import { useViewerStore } from "store/viewer.store"
import { useIsResultCsvPending } from "store/resultCsv.store"
import { useIsResultPdfPending } from "store/resultPdf.store"
import { useIsDetailsWordPending } from "store/detailsWord.store"
import type { ReactNode } from "react"
import type { MenuProps } from "antd"
import type { Core, WebViewerInstance } from "@pdftron/webviewer"
import type { TQuotationZoomDirection } from "store/viewer.store"
import type { TViewerTool } from "../core/viewerTools"
import { setFitModeOnAllPanes, zoomOnAllPanes } from "../core/viewerTools"
import { compactTooltipStyles, getShadowTooltipContainer } from "../shared/shadowTooltip"
import style from "./CustomWebViewerHeader.module.scss"
// import { AIAgentViewerIcon } from "components/ui/icons/iaAgent/AIAgentViewerIcon"

const { addMenuItem, menuRow, menuCheck, menuIcon, menuLabel, menuItemRow, menuItemHint, shapeMenuCheck } = style

// One entry of a header dropdown. `run` absent = the entry renders disabled (an epic step whose
// handler isn't wired yet); `group` is only used by the + Add menu, which is split in two; `checked`
// marks a row that carries a checkmark — the Shapes menu's currently-armed tool, and the dual-screen
// menu's "Auto open floor plan" toggle.
type THeaderMenuEntry = {
	key: string
	label: string
	icon?: ReactNode
	group?: "references" | "furniture"
	checked?: boolean
	run?: () => void
	// Force the row off even though a handler exists. Missing `run` still means disabled (that is
	// how the + Add menu greys the REQs that aren't wired yet), but the dual-view menu needs rows
	// that are WIRED and momentarily inapplicable — `Close left view` while the second view is in
	// its own window, say. Those keep their handler, and their number, and simply cannot be picked
	// (#1430).
	disabled?: boolean
}

// Whether a row can be activated at all. One rule, used by the antd item, the digit registration
// and nothing else — so a row can never be clickable and un-pickable, or the reverse.
const isEntryDisabled = (entry: THeaderMenuEntry): boolean => entry.disabled === true || !entry.run

// The shape viewerMenus.store keeps: position, whether it can be activated, and what to run. The
// icons and labels stay behind in the header — the store only needs to know what a digit does.
const toRegisteredItems = (entries: THeaderMenuEntry[]): TViewerMenuItem[] =>
	entries.map((entry) => ({ key: entry.key, disabled: isEntryDisabled(entry), run: () => entry.run?.() }))

type ViewerTool = "select" | "pan" | "zoom" | "ruler"

type Props = {
	instance: WebViewerInstance
	onToolChange: (tool: TViewerTool, zoomDirection?: TQuotationZoomDirection) => void
	onToggleRulerTool: () => void
	onClose?: () => void
	onShareFeedback?: () => void
	// Opens the keyboard-shortcuts panel (REQ-359) from the help menu. Like the callbacks below it
	// must be referentially stable — the header's render() callback captures its props once.
	onOpenKeyboardShortcuts?: () => void
	onSettings?: () => void
	// Starts the on-demand result CSV download (REQ-420). Progress is read from the shared store
	// below, not from a prop: this header is rendered once into the shadow DOM by Apryse's
	// customElement `render()`, so a reactive prop would go stale.
	onDownloadResultCsv?: () => Promise<void>
	// Starts the on-demand annotated result PDF download (#1401). Same shape/reasoning as
	// onDownloadResultCsv above.
	onDownloadResultPdf?: () => Promise<void>
	// Starts the on-demand Quotation Details Word file download (#1448). Same shape/reasoning as
	// onDownloadResultCsv above.
	onDownloadDetailsWord?: () => Promise<void>
	// Turns split screen on/off (REQ-399). Since #1311 decision D10 this is driven by the LAYOUT
	// menu's pane-count rows, not by the split button — `Single Page` asks for one pane,
	// `Side-by-side` for two — so it takes the state it wants rather than toggling. The orchestrator
	// omits it when VITE_SPLIT_SCREEN=off, which hides the split control entirely and leaves both
	// pane-count rows disabled; the flag is constant for the session, so it is the only split-screen
	// state that CAN be a prop. Everything dynamic (active / pending / available) is read from the
	// store instead: Apryse's header `render()` callback captures props once, so a prop would never
	// update. See splitScreen.store.
	onSetSplitActive?: (active: boolean) => void
	// Split-view menu's "Close left view" / "Close right view" rows (decision D3 follow-up): each
	// discards one pane, keeping the OTHER pane's page/zoom/scroll. "Close right view" used to reuse
	// onSetSplitActive(false) on the assumption that leaving split screen naturally lands on the left
	// pane's own viewport — reported broken (PR #1347 review), so it now gets the same dedicated,
	// snapshot-and-restore treatment as "Close left view" rather than trusting that assumption.
	onCloseLeftView?: () => void
	onCloseRightView?: () => void
	// "Dual view in new tab" — opens the second view in its own browser window (#1430). Omitted when
	// VITE_POP_OUT_WINDOW=off, which removes the row; everything dynamic (open / pending /
	// available) is read from popOutWindow.store for the usual reason — this header's `render()`
	// captures its props once.
	onPopOut?: () => void
	// "Return right view back" (this window) / "Return view back" (inside the pop-out): closes the
	// pop-out and puts the second view back in the main window. One handler for both, because it is
	// one behaviour — only the label differs, and which label is a property of which window we are.
	onReturnViewBack?: () => void
	// Opens the "add elevation reference" mode from the + Add menu (REQ-292). The menu's remaining
	// entries are the rest of the epic (REQ-295+) and ship disabled until they have handlers.
	onAddElevationReference?: () => void
	// The rest of the + Add menu: countertop (REQ-298), elevation callout (REQ-294), cabinet
	// (REQ-293), other object (REQ-299) and custom object (#1371).
	onAddCountertop?: () => void
	onAddElevationCallout?: () => void
	onAddCabinet?: () => void
	onAddOther?: () => void
	onAddCustom?: () => void
	// REQ-395/396 note tool: arms/disarms Apryse's native FreeTextAnnotation create tool. Dynamic
	// state (armed, selected note, font size / text color) is read from `noteTool.store` rather
	// than props — same reason `splitScreen.store` exists: this header's render() captures its
	// props once.
	onToggleNoteTool?: () => void
	// The tool-options row's close (X) button — disarms the tool while drawing, or deselects the
	// currently selected note when the row is up because of a selection instead.
	onCloseNoteToolbar?: () => void
	// Fired in the SAME commit the tool-options row appears/disappears, before paint. That row is in
	// the header's layout flow, so it resizes the document area — and the overlays anchored to that
	// area (the sidebar toggle, the page-nav bar) would otherwise only catch up a frame later when
	// the ResizeObserver fires, producing a second visible jump right after the first. Must be
	// referentially STABLE: Apryse renders this header once and captures its props then.
	onToolOptionsRowToggle?: () => void
	// REQ-400 figure highlight tool: arms Apryse's native Rectangle/EllipseAnnotation create tool
	// (Shapes dropdown, or the R/O hotkeys) and leaves it (the tool-options bar's close button).
	// Dynamic state (which shape is armed, the chosen color) is read from figureHighlight.store
	// rather than props — same reason splitScreen.store exists: this header's render() captures its
	// props once.
	onArmShapeTool?: (shape: TFigureShapeType) => void
	onDisarmShapeTool?: () => void
	// REQ-400 #1324 freehand highlight tool. A single TOGGLE (unlike the shape tool's arm/disarm
	// pair): the toolbar button is visibly active while armed and clicking it again exits. The
	// options row's ✕ reuses this too — that row only renders while armed, so toggling from it is
	// always a disarm. Armed state itself is read from figureHighlight.store, not passed as a prop —
	// Apryse's render() captures props once.
	onToggleFreehandTool?: () => void
}

const getZoomPercent = (documentViewer: Core.DocumentViewer) => {
	const zoomLevel = documentViewer.getZoomLevel?.()
	return typeof zoomLevel === "number" ? `${Math.round(zoomLevel * 100)}%` : "100%"
}

const isSelectedAnnotationPageVisible = (
	documentViewer: Core.DocumentViewer,
	annotation: Core.Annotations.Annotation,
): boolean => {
	const visiblePages = documentViewer.getDisplayModeManager?.()?.getDisplayMode?.()?.getVisiblePages?.()
	return visiblePages?.includes(annotation.PageNumber) === true
}

const ZOOM_STEP_FACTOR = 1.25
const PREDEFINED_ZOOM_LEVELS = [10, 25, 50, 100, 125, 150, 200, 400, 800, 1600, 6400]

const ZoomControls = ({
	active,
	activeDirection,
	zoomPercent,
	onZoomIn,
	onZoomOut,
	onZoomInStep,
	onZoomOutStep,
	onZoomScaleChange,
}: {
	active: boolean
	activeDirection: TQuotationZoomDirection
	zoomPercent: string
	onZoomIn: () => void
	onZoomOut: () => void
	onZoomInStep: () => void
	onZoomOutStep: () => void
	onZoomScaleChange: (scale: string) => void
}) => {
	const [isScaleMenuOpen, setIsScaleMenuOpen] = useState(false)
	const zoomLevelRef = useRef<HTMLDivElement>(null)
	const scaleMenuItems: MenuProps["items"] = [
		{
			key: "fit-width",
			icon: (
				<span className="wv-zoom-menu-icon">
					<FitToWidthIcon />
				</span>
			),
			label: "Fit to width",
			onClick: () => onZoomScaleChange("Fit to width"),
		},
		{
			key: "fit-page",
			icon: (
				<span className="wv-zoom-menu-icon">
					<FitToPageIcon />
				</span>
			),
			label: "Fit to page",
			onClick: () => onZoomScaleChange("Fit to page"),
		},
		{ type: "divider", key: "fit-divider" },
		...PREDEFINED_ZOOM_LEVELS.map((level) => ({
			key: `zoom-${level}`,
			label: `${level}%`,
			onClick: () => onZoomScaleChange(`${level}%`),
		})),
	]

	useEffect(() => {
		if (!isScaleMenuOpen) return
		const closeOnOutsideMouseDown = (event: MouseEvent) => {
			const path = event.composedPath()
			const clickedZoomLevel = zoomLevelRef.current !== null && path.includes(zoomLevelRef.current)
			const clickedMenu = path.some(
				(target) => target instanceof Element && target.classList.contains("wv-zoom-level-dropdown"),
			)
			if (!clickedZoomLevel && !clickedMenu) setIsScaleMenuOpen(false)
		}
		window.addEventListener("mousedown", closeOnOutsideMouseDown, true)
		return () => window.removeEventListener("mousedown", closeOnOutsideMouseDown, true)
	}, [isScaleMenuOpen])

	return (
		<div className="wv-zoom-tools">
			<div className="wv-zoom-mode-pair">
				<Tooltip
					title="Zoom in (Z)"
					placement="bottom"
					getPopupContainer={getShadowTooltipContainer}
					styles={compactTooltipStyles}
				>
					<button
						className={`wv-btn-tools wv-btn-viewer-tool${active && activeDirection === "in" ? " wv-btn-viewer-tool--active" : ""}`}
						onClick={onZoomIn}
						aria-label="Zoom in tool"
					>
						<img className="wv-zoom-tool-icon" src={zoomInIcon} alt="" />
					</button>
				</Tooltip>
				<Tooltip
					title="Zoom out (Shift + Z)"
					placement="bottom"
					getPopupContainer={getShadowTooltipContainer}
					styles={compactTooltipStyles}
				>
					<button
						className={`wv-btn-tools wv-btn-viewer-tool${active && activeDirection === "out" ? " wv-btn-viewer-tool--active" : ""}`}
						onClick={onZoomOut}
						aria-label="Zoom out tool"
					>
						<img className="wv-zoom-tool-icon" src={zoomOutIcon} alt="" />
					</button>
				</Tooltip>
			</div>
			<Dropdown
				items={scaleMenuItems}
				placement="bottomLeft"
				overlayStyle={{ zIndex: 1500, width: 170 }}
				overlayClassName="wv-zoom-level-dropdown"
				open={isScaleMenuOpen}
				onOpenChange={(open, info) => {
					if (info.source !== "menu") setIsScaleMenuOpen(open)
				}}
				onClick={() => setIsScaleMenuOpen(true)}
				trigger={[]}
			>
				<div className="wv-zoom-level" ref={zoomLevelRef}>
					<button
						className="wv-zoom-level__scale"
						aria-label="Select zoom level"
						onClick={() => setIsScaleMenuOpen(!isScaleMenuOpen)}
					>
						<span className="wv-zoom-level__value">{zoomPercent}</span>
						<img
							className={`wv-zoom-chevron-icon${isScaleMenuOpen ? " wv-zoom-chevron-icon--open" : ""}`}
							src={chevronDownIcon}
							alt=""
						/>
					</button>
					<div className="wv-zoom-level__actions">
						<button className="wv-zoom-level__button" onClick={onZoomInStep} aria-label="Zoom in level">
							<img className="wv-zoom-level-icon" src={circlePlusIcon} alt="" />
						</button>
						<button className="wv-zoom-level__button" onClick={onZoomOutStep} aria-label="Zoom out level">
							<img className="wv-zoom-level-icon" src={circleMinusIcon} alt="" />
						</button>
					</div>
				</div>
			</Dropdown>
		</div>
	)
}

export const CustomWebViewerHeader = ({
	instance,
	onToolChange,
	onToggleRulerTool,
	onClose,
	onShareFeedback,
	onOpenKeyboardShortcuts,
	onSettings,
	onDownloadResultCsv,
	onDownloadResultPdf,
	onDownloadDetailsWord,
	onSetSplitActive,
	onCloseLeftView,
	onCloseRightView,
	onPopOut,
	onReturnViewBack,
	onAddElevationReference,
	onAddCountertop,
	onAddElevationCallout,
	onAddCabinet,
	onAddOther,
	onAddCustom,
	onToggleNoteTool,
	onCloseNoteToolbar,
	onToolOptionsRowToggle,
	onArmShapeTool,
	onDisarmShapeTool,
	onToggleFreehandTool,
}: Props) => {
	const { Core } = instance
	const isNoteToolArmed = useNoteToolStore((state) => state.isArmed)
	const activeNoteId = useNoteToolStore((state) => state.activeNoteId)
	const selectedNoteFontSize = useNoteToolStore((state) => state.selectedFontSize)
	const setSelectedNoteFontSize = useNoteToolStore((state) => state.setSelectedFontSize)
	const selectedNoteTextColor = useNoteToolStore((state) => state.selectedTextColor)
	const setSelectedNoteTextColor = useNoteToolStore((state) => state.setSelectedTextColor)
	const activeShapeTool = useFigureHighlightStore((state) => state.activeShapeTool)
	const selectedStrokeColor = useFigureHighlightStore((state) => state.selectedStrokeColor)
	const setSelectedStrokeColor = useFigureHighlightStore((state) => state.setSelectedStrokeColor)
	const selectedFillColor = useFigureHighlightStore((state) => state.selectedFillColor)
	const setSelectedFillColor = useFigureHighlightStore((state) => state.setSelectedFillColor)
	const selectedOpacity = useFigureHighlightStore((state) => state.selectedOpacity)
	const setSelectedOpacity = useFigureHighlightStore((state) => state.setSelectedOpacity)
	const selectedStrokeWidth = useFigureHighlightStore((state) => state.selectedStrokeWidth)
	const setSelectedStrokeWidth = useFigureHighlightStore((state) => state.setSelectedStrokeWidth)
	const selectedLineStyle = useFigureHighlightStore((state) => state.selectedLineStyle)
	const setSelectedLineStyle = useFigureHighlightStore((state) => state.setSelectedLineStyle)
	// Which line styles the ARMED shape cannot render (see the LineStylePicker usage below). Keyed on
	// activeShapeTool only — the service's answer is a property of the SDK build, memoized there.
	const cloudyUnsupportedStyles = useMemo(
		() => (activeShapeTool && !AnnotationService.supportsCloudyBorder(activeShapeTool) ? [CLOUDY_LINE_STYLE] : []),
		[activeShapeTool],
	)
	const isFreehandArmed = useFigureHighlightStore((state) => state.isFreehandArmed)
	const freehandColor = useFigureHighlightStore((state) => state.freehandColor)
	const setFreehandColor = useFigureHighlightStore((state) => state.setFreehandColor)
	const freehandOpacity = useFigureHighlightStore((state) => state.freehandOpacity)
	const setFreehandOpacity = useFigureHighlightStore((state) => state.setFreehandOpacity)
	const freehandStrokeWidth = useFigureHighlightStore((state) => state.freehandStrokeWidth)
	const setFreehandStrokeWidth = useFigureHighlightStore((state) => state.setFreehandStrokeWidth)
	const isSplitActive = useSplitScreenStore((state) => state.isSplitActive)
	const isSplitPending = useSplitScreenStore((state) => state.isSplitPending)
	const isSplitAvailable = useSplitScreenStore((state) => state.isSplitAvailable)
	const layoutEpoch = useSplitScreenStore((state) => state.layoutEpoch)
	const quotationZoomDirection = useViewerStore((state) => state.quotationZoomDirection)
	const isAutoOpenFloorPlan = useSplitScreenStore((state) => state.isAutoOpenFloorPlan)
	const toggleAutoOpenFloorPlan = useSplitScreenStore((state) => state.toggleAutoOpenFloorPlan)
	// Pop-out state (#1430). In the MAIN window these say whether a pop-out is up; inside the
	// pop-out `isPopOutOpen` is true of itself, which is what puts its own menu into the
	// popped-out state. From the store, not props — the header captures its props once.
	const isPopOutOpen = usePopOutWindowStore((state) => state.isPopOutOpen)
	const isPopOutPending = usePopOutWindowStore((state) => state.isPopOutPending)
	const isPopOutAvailable = usePopOutWindowStore((state) => state.isPopOutAvailable)
	const [isDropdownOpen, setIsDropdownOpen] = useState(false)
	const [isViewControlsOpen, setIsViewControlsOpen] = useState(false)
	// Which header dropdown is open lives in a store, not in local state: the A / H / D hotkeys are
	// bound outside this component, which Apryse renders once (so a prop or a setState reference
	// handed out from here would be captured once and go stale). See viewerMenus.store.
	const openHeaderMenu = useViewerMenusStore((state) => state.openMenu)
	const setOpenMenu = useViewerMenusStore((state) => state.setOpenMenu)
	const closeMenu = useViewerMenusStore((state) => state.closeMenu)
	const toggleMenu = useViewerMenusStore((state) => state.toggleMenu)
	const registerMenuItems = useViewerMenusStore((state) => state.registerMenuItems)
	// The dual-view menu moved into that same store (#1383) so the D hotkey can open it and its rows
	// can be picked by digit.
	//
	// It used to be gated on `isSplitActive` here, because the control was asymmetric: with one pane
	// the same button entered split view instead of opening anything (#1311 D11). #1430 removes that
	// asymmetry — with TWO ways to get a second view (in-window and popped out) a single click cannot
	// stand for both, so the control now always opens the menu and the menu's first two rows are the
	// choice. Hence no gate: the menu exists in every state.
	const isSplitMenuOpen = openHeaderMenu === "splitView"
	const [isRulerActive, setIsRulerActive] = useState(false)
	const [activeTool, setActiveTool] = useState<ViewerTool>("select")
	const [zoomPercent, setZoomPercent] = useState(() => getZoomPercent(Core.documentViewer))
	// Store-backed, for the same reason the ruler re-reads the real tool mode: this header is
	// re-rooted on Apryse re-renders, so local state would drop mid-generation and re-enable the item.
	const isCsvPending = useIsResultCsvPending()
	const isPdfPending = useIsResultPdfPending()
	const isDetailsWordPending = useIsDetailsWordPending()

	const zoomAroundSelectedAnnotationIfVisible = (nextZoom: number) => {
		const { annotationManager, documentViewer } = instance.Core
		const selectedAnnotation = annotationManager.getSelectedAnnotations()[0]

		if (
			selectedAnnotation === undefined ||
			isSelectedAnnotationPageVisible(documentViewer, selectedAnnotation) === false
		) {
			return
		}

		// Run after zoomOnAllPanes' viewport-centering frame so the selected annotation
		// remains the focus only when it belongs to the current page.
		window.requestAnimationFrame(() => {
			annotationManager.jumpToAnnotation(selectedAnnotation, {
				horizontalOffset: "50%",
				verticalOffset: "50%",
				zoom: nextZoom,
			})
		})
	}

	const applyZoom = (nextZoom: number) => {
		zoomOnAllPanes(instance, nextZoom)
		zoomAroundSelectedAnnotationIfVisible(nextZoom)
	}

	const zoomByStep = (shouldZoomOut: boolean) => {
		const { documentViewer } = instance.Core
		const currentZoom = documentViewer.getZoomLevel()
		if (currentZoom <= 0) return

		const nextZoom = currentZoom * (shouldZoomOut ? 1 / ZOOM_STEP_FACTOR : ZOOM_STEP_FACTOR)
		const clampedZoom = Math.max(instance.UI.getMinZoomLevel(), Math.min(instance.UI.getMaxZoomLevel(), nextZoom))
		if (clampedZoom === currentZoom) return

		applyZoom(clampedZoom)
	}

	const setZoomScale = (scale: string) => {
		if (scale === "Fit to width") {
			setFitModeOnAllPanes(instance, "FitWidth")
			return
		}
		if (scale === "Fit to page") {
			setFitModeOnAllPanes(instance, "FitPage")
			return
		}

		const zoomLevel = Number.parseInt(scale, 10) / 100
		if (Number.isNaN(zoomLevel)) return
		const clampedZoom = Math.max(instance.UI.getMinZoomLevel(), Math.min(instance.UI.getMaxZoomLevel(), zoomLevel))
		applyZoom(clampedZoom)
	}

	// Keep the tool buttons in sync with the real tool mode. The header is re-rooted (local state
	// resets) on Apryse re-renders, while the actual tool mode persists — so derive the button state
	// from the viewer rather than local state. Reading document viewer 1 is enough: every tool
	// change fans out to all panes (core/viewerTools), so the primary is a faithful proxy. Keyed on
	// layoutEpoch because MultiViewerMode re-mounts the viewers on split enter/exit.
	useEffect(() => {
		const documentViewer = Core.documentViewer
		const editTool = documentViewer.getTool(Core.Tools.ToolNames.EDIT)
		const panTool = documentViewer.getTool(Core.Tools.ToolNames.PAN)
		const zoomTool = documentViewer.getTool(Core.Tools.ToolNames.MARQUEE)
		const measurementTool = documentViewer.getTool(Core.Tools.ToolNames.DISTANCE_MEASUREMENT)
		const syncFromTool = () => {
			const toolMode = documentViewer.getToolMode()
			setIsRulerActive(toolMode === measurementTool)
			if (toolMode === panTool) setActiveTool("pan")
			else if (toolMode === zoomTool) setActiveTool("zoom")
			else if (toolMode === measurementTool) setActiveTool("ruler")
			else if (toolMode === editTool) setActiveTool("select")
		}

		syncFromTool()
		documentViewer.addEventListener("toolModeUpdated", syncFromTool)
		// The zoom readout is viewer 1's, for the same reason the tool state above is: there is ONE
		// `zoomPercent` and it cannot describe two panes. Listening on every pane wrote that one state
		// from whichever viewer fired last — always the split pane, since every fan-out iterates the
		// viewers in order — so the header showed the RIGHT pane's zoom while labelling it the
		// document's (measured: 12% while the left pane sat at 29.8%). #1415 AC6 pins it to the
		// primary (LEFT) viewer. Reading viewer 1 is licensed because every zoom WRITE fans out
		// (`zoomOnAllPanes` / `setFitModeOnAllPanes`), so the primary is a faithful proxy; note this
		// is the viewer axis, not the Primary/Secondary ROLE smart navigation flips.
		const syncZoom = () => setZoomPercent(getZoomPercent(documentViewer))
		syncZoom()
		documentViewer.addEventListener("zoomUpdated", syncZoom)
		return () => {
			documentViewer.removeEventListener("toolModeUpdated", syncFromTool)
			documentViewer.removeEventListener("zoomUpdated", syncZoom)
		}
	}, [Core, layoutEpoch])

	// The layout control is inert while dual screen is up (#1383 review): its rows are about the
	// pane count, and with two panes the only one that would do anything is `Single Page`. The way
	// back to one pane is the dual-screen menu's own `Close left view` / `Close right view`, which
	// additionally keep the surviving pane's page/zoom/scroll — something `Single Page` never did.
	//
	// It is inert while POPPED OUT too (#1430). This window has one pane then, so `Side-by-side`
	// would happily open a second — leaving three views of the drawing, which is not a state the
	// design has, the menu can describe, or `Return right view back` can unwind. The way back is that
	// row.
	const isLayoutMenuDisabled = isSplitActive || isPopOutOpen

	// Entering dual screen while the layout menu happens to be open would leave a menu on screen that
	// the guard below has just stopped controlling — and a stale `true` would spring it open again on
	// exit. `applyPaneCount` covers the click path; this covers the D hotkey and every other entry.
	useEffect(() => {
		if (isLayoutMenuDisabled) setIsViewControlsOpen(false)
	}, [isLayoutMenuDisabled])

	// Ask the viewer for a PANE COUNT — from the layout menu's two rows (decision D10) or from the
	// split button itself when there is one pane. Idempotent the whole way down —
	// `useSplitScreen.setSplit` no-ops when split screen is already in the requested state — so
	// clicking the row that is already checked just closes the menu.
	const applyPaneCount = (wantSplit: boolean) => {
		onSetSplitActive?.(wantSplit)
		setIsViewControlsOpen(false)
	}

	// The burger menu is the on-demand result downloads and nothing else (#1383 review). It used to
	// also carry Download / Save As / Print, which are Apryse's own `UI.downloadPdf()` / `UI.print()`
	// on the source PDF — none of them a flow this product supports, and Download and Save As were
	// the SAME call under two labels. They are deleted rather than disabled: a greyed row implies "not
	// yet", and there is no plan to wire these up.
	//
	// The CSV (REQ-420), the annotated PDF (#1401) and the Quotation Details Word file (#1448) are
	// independent rows: each is gated on its own prop and its own pending flag, so any of them can be
	// offered/generating without affecting the others.
	const menuItems: MenuProps["items"] = [
		...(onDownloadResultCsv
			? [
					{
						key: "downloadResultCsv",
						// Progress is carried by the toast the hook raises, which outlives this menu closing on
						// click; the label here is what a user who reopens the menu mid-generation sees.
						label: isCsvPending ? RESULT_CSV_MESSAGES.pending : RESULT_CSV_MESSAGES.menuItem,
						disabled: isCsvPending,
						onClick: onDownloadResultCsv,
					},
				]
			: []),
		...(onDownloadResultPdf
			? [
					{
						key: "downloadResultPdf",
						label: isPdfPending ? RESULT_PDF_MESSAGES.pending : RESULT_PDF_MESSAGES.menuItem,
						disabled: isPdfPending,
						onClick: onDownloadResultPdf,
					},
				]
			: []),
		...(onDownloadDetailsWord
			? [
					{
						key: "downloadDetailsWord",
						label: isDetailsWordPending ? DETAILS_WORD_MESSAGES.pending : DETAILS_WORD_MESSAGES.menuItem,
						disabled: isDetailsWordPending,
						onClick: onDownloadDetailsWord,
					},
				]
			: []),
	]

	// A checkable menu row, laid out to the design: a fixed 16px check slot, then the row's own
	// leading icon, then the label. The whole thing goes into antd's `label` rather than its `icon`
	// prop for the reason documented on `.addMenuItem` in the stylesheet — antd clones the `icon`
	// node with a className our icon components drop, which loses the spacing.
	//
	// `position` adds the keyboard digit on the far side, exactly as buildMenuItem draws it for the
	// Add / help / Shapes menus (#1383). It is optional because only menus the 1-5 digit-pick can
	// reach get one: the split-view menu does, the LAYOUT menu (which has no hotkey of its own)
	// deliberately does not, and a number beside a row no key activates would be a lie.
	const renderMenuRow = (checked: boolean, icon: ReactNode, label: string, position?: number) => {
		const row = (
			<span className={menuRow}>
				<span className={menuCheck}>{checked ? <CheckIcon /> : null}</span>
				<span className={menuIcon}>{icon}</span>
				<span className={menuLabel}>{label}</span>
			</span>
		)
		if (position === undefined) return row
		return (
			<span className={menuItemRow}>
				{row}
				{/* aria-hidden for the same reason as buildMenuItem's hint: in the accessible name it
				    reads as part of the label ("Close left view 1"). `data-menu-hint` is
				    the hook webviewer-quotation.css scopes its alignment rule off — module
				    class names are hashed, so a plain stylesheet cannot name `.menuItemRow`. */}
				<span className={menuItemHint} data-menu-hint="" aria-hidden="true">
					{position}
				</span>
			</span>
		)
	}

	// Is the dual-view CONTROL rendered at all? Either feature behind it being on is enough (#1430):
	// the two flags are independent, so `VITE_SPLIT_SCREEN=off` with the pop-out on must still leave
	// a control to open the pop-out from — hiding it would make that flag combination unreachable
	// rather than just reduced. The `Dual view` row then has no handler and greys itself.
	const isSplitFeatureEnabled =
		onSetSplitActive !== undefined || onPopOut !== undefined || onReturnViewBack !== undefined
	const canChangePaneCount = onSetSplitActive !== undefined && !isSplitPending

	// Layout dropdown (#1311 AC1/AC2, decision D10). These four rows are about HOW MANY PANES there
	// are, not about the page-scroll mode: `Single Page` means one pane, `Side-by-side` means two, and
	// the checkmark reports the live pane count. The two `Continuous …` rows are the design's, drawn
	// so the menu never changes shape again, and ship disabled as unsupported.
	//
	// Nothing here calls `UI.setLayoutMode` any more. That matters beyond tidiness: the editor relies
	// on Apryse's continuous scrolling for cross-page navigation (AnnotationService.frameAnnotations
	// reaches another page by scrolling the pane, which a single-page layout would make impossible),
	// so the header must not be able to change it. Note the deliberate lowercase `s` in
	// "Side-by-side".
	const viewControlItems: MenuProps["items"] = [
		{
			key: "single-page",
			label: renderMenuRow(!isSplitActive, <LayoutSinglePageIcon />, "Single Page"),
			disabled: !canChangePaneCount,
			onClick: () => applyPaneCount(false),
		},
		{
			key: "facing",
			label: renderMenuRow(isSplitActive, <LayoutFacingIcon />, "Side-by-side"),
			// Entering also needs the viewer to be ready (editor mode, document loaded); leaving never
			// does, and `Single Page` above is the only way out, so it is never gated on availability.
			disabled: !canChangePaneCount || (!isSplitAvailable && !isSplitActive),
			onClick: () => applyPaneCount(true),
		},
	]

	// The DUAL-VIEW menu — the control's own dropdown (#1311 AC5/AC6, extended by #1430).
	//
	// "Reverse view" and "Synchronize scrolling" were dropped from the design (PR #1347 review)
	// rather than shipped disabled — neither had a confirmed use case, so there was nothing to keep
	// a placeholder row for. #1430 confirms that: both are explicitly out of scope.
	//
	// Both Close-view rows get their own dedicated prop rather than trusting exitMultiViewerMode's
	// natural collapse target — see useSplitScreen's closeLeftView/closeRightView.
	//
	// #1383: built from ONE descriptor list, like the + Add / help / Shapes menus below — the antd
	// rows, the digit drawn beside each and the entry the digit shortcuts activate can then never
	// disagree.
	//
	// EVERY row dismisses the menu, "Auto open floor plan" included. The #1311 design kept that one
	// open so its checkmark could be seen changing; review rejected that (#1383), so the row now
	// behaves like every other menu entry in the editor and the checkmark is read on the next open.
	//
	// #1430 gives the menu FOUR shapes, which is the whole of the feature's UI:
	//
	//   S1  one pane          1 Dual view · 2 Dual view in new tab
	//   S2  split in-window   the above + Close left/right view + Auto open floor plan
	//   S3  popped out, main  as S2, but Dual view checked-and-inert, row 2 = Return right view
	//                         back, and Close/Auto-open all inert
	//   S4  popped out, child same as S3 with row 2 reading "Return view back" and Dual view
	//                         unchecked — this window has one pane and may never have two
	//
	// The pane rows are ABSENT in S1 rather than disabled, per the design: with one pane and nothing
	// popped out there are no two views for them to talk about, and three greyed rows would be noise
	// under the two that actually do something. From S2 on they exist, so their numbers stay put for
	// as long as they are visible.
	const isPoppedOut = isPopOutOpen
	// Row 2 flips identity — out, or back. `onReturnViewBack` serves both windows; the LABEL is what
	// differs ("Return RIGHT view back" only makes sense where there is a right side), and which
	// label applies is a property of WHICH WINDOW this is.
	//
	// Asked of the route directly rather than inferred from the store flags. It is not dynamic state
	// — it is constant for this window's entire life — so none of the "the header captures its props
	// once" constraints apply, and a direct question cannot be got wrong the way "available is false,
	// so we must be the child" can (availability also goes false with the document unloaded).
	const isMainWindow = !isPopOutWindowRoute()
	const splitViewEntries = useMemo<THeaderMenuEntry[]>(() => {
		const dualViewRow: THeaderMenuEntry = {
			key: "dual-view",
			label: "Dual view",
			icon: <LayoutFacingIcon />,
			// Checked whenever a second view EXISTS — in this window's own split, or popped out into
			// its own window. Reading `isSplitActive` alone left S3 unchecked: popping out collapses
			// the split first (that is AC8), so by the time the menu is in its popped-out state the
			// flag is false. The checkmark is the only thing on screen saying "there is a second view,
			// it just isn't in here", which is why the design specifies it and browser verification
			// flagged its absence. The POP-OUT's own menu stays unchecked — that window has one pane
			// and may never have two.
			checked: isSplitActive || (isPoppedOut && isMainWindow),
			// Asks for the state, idempotently, exactly as the layout menu's rows do — it is not a
			// toggle. The ways OUT stay the Close rows and the layout menu's `Single Page`, which is
			// what #1383 settled and what keeps a mis-picked digit from collapsing a pane.
			//
			// Left UNDEFINED when the in-window split is flagged off, rather than wrapped in an
			// optional call: a `run` that exists and does nothing would render an enabled row that
			// silently ignores clicks, and `isEntryDisabled` reads exactly this.
			run: onSetSplitActive ? () => onSetSplitActive(true) : undefined,
			// Inert while popped out: the second view is already open, just not here, and entering an
			// in-window split on top of it would make three. Inert on a single-page document too —
			// there is nothing to put in a second pane.
			disabled: isPoppedOut || isSplitPending || (!isSplitAvailable && !isSplitActive),
		}

		const secondRow: THeaderMenuEntry = isPoppedOut
			? {
					key: "return-view-back",
					label: isMainWindow ? "Return right view back" : "Return view back",
					icon: <ReturnViewBackIcon />,
					run: onReturnViewBack,
				}
			: {
					key: "pop-out-view",
					label: "Dual view in new tab",
					icon: <PopOutViewIcon />,
					run: onPopOut,
					// Pending covers the window between the click and the browser answering, so the row
					// cannot be picked twice into two windows.
					disabled: isPopOutPending || !isPopOutAvailable,
				}

		if (!isSplitActive && !isPoppedOut) return [dualViewRow, secondRow]

		return [
			dualViewRow,
			secondRow,
			{
				key: "close-left-view",
				label: "Close left view",
				icon: <CloseLeftViewIcon />,
				run: onCloseLeftView,
				// Popped out there is no left/right pair in this window to close — the second view is a
				// window, and `Return right view back` is how it comes home.
				disabled: isPoppedOut,
			},
			{
				key: "close-right-view",
				label: "Close right view",
				icon: <CloseRightViewIcon />,
				run: onCloseRightView,
				disabled: isPoppedOut,
			},
			{
				key: "auto-open-floor-plan",
				label: "Auto open floor plan",
				icon: <AutoOpenFloorPlanIcon />,
				checked: isAutoOpenFloorPlan,
				run: toggleAutoOpenFloorPlan,
				// Live in BOTH layouts: in-window split flips the Primary/Secondary role between two
				// PANES (useSmartNavigation), while popped out it drives the cross-window counterpart
				// instead (usePopOutFollowNavigation) — a selection in one REALM posts over the pop-out's
				// own control channel, and the other realm auto-displays the connected counterpart the
				// same way. One flag, one row, whichever mechanism the current layout actually has.
				disabled: false,
			},
		]
	}, [
		isAutoOpenFloorPlan,
		// Constant for this window's life (it is the route), but listed so the exhaustive-deps rule
		// stays satisfied without an eslint-disable — a suppression here would also hide a real
		// missing dep the next time this list changes.
		isMainWindow,
		isPopOutAvailable,
		isPopOutPending,
		isPoppedOut,
		isSplitActive,
		isSplitAvailable,
		isSplitPending,
		onCloseLeftView,
		onCloseRightView,
		onPopOut,
		onReturnViewBack,
		onSetSplitActive,
		toggleAutoOpenFloorPlan,
	])

	// The dividers separate the two "how do I get a second view" rows from the pane rows, and those
	// from the toggle. They are NOT entries: positions are counted over `splitViewEntries`, so a
	// divider can move without renumbering anything.
	const splitViewItems: MenuProps["items"] = splitViewEntries.flatMap((entry, index) => {
		const row = {
			key: entry.key,
			label: renderMenuRow(entry.checked === true, entry.icon, entry.label, index + 1),
			disabled: isEntryDisabled(entry),
			// Close-then-run, exactly as viewerMenus.store's pickOpenMenuItem does for the digit, so the
			// two paths cannot drift. The Dropdown's own onOpenChange ignores a `source: "menu"` change,
			// which is what leaves that decision here rather than to antd.
			onClick: () => {
				closeMenu("splitView")
				entry.run?.()
			},
		}
		return entry.key === "close-left-view" || entry.key === "auto-open-floor-plan"
			? [{ key: `split-divider-${entry.key}`, type: "divider" as const }, row]
			: [row]
	})

	// The + Add menu (REQ-292..299 "detection addition") and the help menu behind the "?", both built
	// from ONE descriptor list each. That list is also what gets registered into viewerMenus.store,
	// so the antd items, the keyboard hints drawn next to them and the entry the 1-6 shortcuts
	// activate can never disagree.
	//
	// A digit is an entry's POSITION in its list, counted across the groups. It stays put as the epic
	// enables the remaining handlers: a disabled entry keeps its number rather than shifting the ones
	// below it, so the numbers don't move under the user's fingers between releases.
	//
	// The icon goes INSIDE the label instead of into antd's `icon` prop — see the module's comment.
	const addMenuEntries = useMemo<THeaderMenuEntry[]>(
		() => [
			{
				key: "elevation-reference",
				group: "references",
				label: "Elevation reference",
				icon: <ErefStatusIcon />,
				run: onAddElevationReference,
			},
			{
				key: "elevation-callout",
				group: "references",
				label: "Elevation callout",
				icon: <EcallIcon />,
				run: onAddElevationCallout,
			},
			{ key: "cabinet", group: "furniture", label: "Cabinet", icon: <FurnitureStatusIcon />, run: onAddCabinet },
			{
				key: "countertop",
				group: "furniture",
				label: "Countertop",
				icon: <FurnitureStatusIcon />,
				run: onAddCountertop,
			},
			{ key: "other", group: "furniture", label: "Other", icon: <FurnitureStatusIcon />, run: onAddOther },
			{ key: "custom", group: "furniture", label: "Custom object", icon: <FurnitureStatusIcon />, run: onAddCustom },
		],
		[onAddElevationReference, onAddElevationCallout, onAddCabinet, onAddCountertop, onAddOther, onAddCustom],
	)

	// The design moves "Share feedback" in here alongside "Keyboard shortcuts" rather than keeping it
	// as a separate text button, so the two help affordances live in one place.
	const helpMenuEntries = useMemo<THeaderMenuEntry[]>(
		() => [
			{ key: "share-feedback", label: "Share feedback", run: onShareFeedback },
			{ key: "keyboard-shortcuts", label: "Keyboard shortcuts", run: onOpenKeyboardShortcuts },
		],
		[onShareFeedback, onOpenKeyboardShortcuts],
	)

	// The "Shapes" dropdown (REQ-400, Figma "Shapes / Opened", node 19019:671577 / :671809): only
	// Rectangle/Ellipse are built for this issue, so the menu lists just those two rows rather than
	// carrying disabled placeholders for the unbuilt shapes (Arc/Polygon/Cloud/Line/Polyline/Arrow) —
	// unlike the + Add menu's Countertop entry, there's no committed near-term plan for those shapes
	// yet, so a disabled row would be a placeholder with no target. Built from the same
	// THeaderMenuEntry/buildMenuItem/toRegisteredItems system as Add/Help (#1381 review: the shape
	// hotkeys move from bare R/O to the shared 1-6 digit-pick, same as every other menu here).
	const shapeMenuEntries = useMemo<THeaderMenuEntry[]>(
		() => [
			{
				key: "rectangle",
				label: "Rectangle",
				icon: <RectangleShapeIcon />,
				checked: activeShapeTool === "rectangle",
				run: onArmShapeTool ? () => onArmShapeTool("rectangle") : undefined,
			},
			{
				key: "ellipse",
				label: "Ellipse",
				icon: <EllipseShapeIcon />,
				checked: activeShapeTool === "ellipse",
				run: onArmShapeTool ? () => onArmShapeTool("ellipse") : undefined,
			},
		],
		[activeShapeTool, onArmShapeTool],
	)

	// Publish all three menus so the A / H / S / 1-6 hotkeys can drive them (see viewerMenus.store).
	// Add/Help's descriptors close over referentially-stable callbacks (the header's render()
	// callback captures its props once), so those two only ever register once; Shapes' entries
	// change with activeShapeTool (the checkmark), so it re-registers whenever that does — cheap,
	// and it's what keeps the checkmark correct when the tool is armed from somewhere other than
	// this menu (e.g. a future hotkey).
	useEffect(() => {
		registerMenuItems("add", toRegisteredItems(addMenuEntries))
		registerMenuItems("help", toRegisteredItems(helpMenuEntries))
	}, [registerMenuItems, addMenuEntries, helpMenuEntries])

	useEffect(() => {
		registerMenuItems("shapes", toRegisteredItems(shapeMenuEntries))
	}, [registerMenuItems, shapeMenuEntries])

	// Split-view (#1383). Like Shapes, its descriptors change with the state they display (the
	// "Auto open floor plan" checkmark), so it re-registers whenever that does. Registered
	// unconditionally rather than only while split screen is active: the store is never READ unless
	// the menu is open, and the menu can only open while split screen is (see isSplitMenuOpen), so
	// gating here would buy nothing and add a way for the digits to find an empty list.
	useEffect(() => {
		registerMenuItems("splitView", toRegisteredItems(splitViewEntries))
	}, [registerMenuItems, splitViewEntries])

	const buildMenuItem = (entry: THeaderMenuEntry, position: number): Required<MenuProps>["items"][number] => ({
		key: entry.key,
		label: (
			<span className={menuItemRow}>
				<span className={addMenuItem}>
					{entry.checked !== undefined ? (
						<span className={shapeMenuCheck}>{entry.checked ? <CheckmarkIcon /> : null}</span>
					) : null}
					{entry.icon}
					{entry.label}
				</span>
				{/* aria-hidden: the digit is a visual accelerator, and letting it into the item's
				    accessible name made screen readers (and every name-based query) read
				    "Share feedback 1". The shortcut itself is announced by the help panel. */}
				<span className={menuItemHint} aria-hidden="true">
					{position}
				</span>
			</span>
		),
		disabled: !entry.run,
		onClick: () => {
			setOpenMenu(null)
			entry.run?.()
		},
	})

	const buildGroupChildren = (group: THeaderMenuEntry["group"]) =>
		addMenuEntries
			.map((entry, index) => ({ entry, position: index + 1 }))
			.filter(({ entry }) => entry.group === group)
			.map(({ entry, position }) => buildMenuItem(entry, position))

	const addMenuItems: MenuProps["items"] = [
		{ key: "references", type: "group", label: "References", children: buildGroupChildren("references") },
		{ key: "furniture", type: "group", label: "Furniture", children: buildGroupChildren("furniture") },
	]

	// Can the dual-view menu do ANYTHING right now? It is the button's disabled rule, and it has to
	// be the union of its rows rather than the in-window split's own availability: a single-page
	// document cannot be split, but it can perfectly well be popped out into its own window, and
	// gating the control on `isSplitAvailable` alone (as it was before #1430) would hide the pop-out
	// row behind a disabled button. Popped out, the menu always has `Return … view back`.
	const isDualViewMenuUsable =
		isSplitActive || isPoppedOut || (canChangePaneCount && isSplitAvailable) || isPopOutAvailable

	// The dual-view control's button — always the trigger for the menu (#1430).
	//
	// It used to be asymmetric (#1311 D11): one pane → enter split directly, two panes → open the
	// menu. That could not survive a second way of getting a second view, since one click cannot
	// stand for both "here" and "in its own window". So the click now always opens the menu, in every
	// state, and the `D` hotkey mirrors it exactly as it always has. The icon still reports the live
	// pane count.
	const splitViewButton = (
		<button
			className={`wv-btn-tools wv-btn-icon wv-btn-split${isSplitActive ? " wv-btn-split--active" : ""}`}
			onClick={() => toggleMenu("splitView")}
			disabled={!isDualViewMenuUsable}
			aria-pressed={isSplitActive}
			aria-expanded={isSplitMenuOpen}
			aria-haspopup="menu"
			// "Dual view MENU", not "Dual view": the menu it opens has a row of its own by that name
			// (#1430), and two buttons sharing an accessible name is ambiguous to a screen reader —
			// which is also exactly what the header tests could no longer tell apart.
			aria-label="Dual view menu"
		>
			<SplitViewIcon active={isSplitActive} />
		</button>
	)

	const helpMenuItems: MenuProps["items"] = helpMenuEntries.map((entry, index) => buildMenuItem(entry, index + 1))

	const shapeMenuItems: MenuProps["items"] = shapeMenuEntries.map((entry, index) => buildMenuItem(entry, index + 1))

	// Is a tool-options row up? All three tools share the single row slot in the JSX below
	// (`shapeToolbarRow || freehandToolbarRow || noteToolbarRow`), so exactly one can ever be
	// rendered — but any of them opening or closing changes the header's height.
	//
	// The note row is shown while the note tool is armed (about to draw) OR an existing note is
	// selected (`activeNoteId`): Figma shows no separate UI for editing an already-placed note's font
	// size / text color, and REQ-395/396 calls for both under one "Font size and text color controls
	// only" requirement.
	const isNoteToolbarOpen = isNoteToolArmed || activeNoteId !== null

	// Reposition the document-area overlays in the same commit the row is added/removed, before the
	// browser paints — see `onToolOptionsRowToggle`'s own comment. `useLayoutEffect`, not
	// `useEffect`: a passive effect lands after paint, which is the second jump we're removing.
	const isToolOptionsRowOpen = isNoteToolbarOpen || activeShapeTool !== null || isFreehandArmed
	useLayoutEffect(() => {
		onToolOptionsRowToggle?.()
	}, [isToolOptionsRowOpen, onToolOptionsRowToggle])

	// ONE tool-options bar, shared by all three tools (#1324 shape/pen + REQ-395/396 note). Each
	// shows a different LABEL and a different set of controls, but the bar itself — the SECOND 44px
	// header row stacked BELOW the main header (which stays fully visible above it), the label slot,
	// the divider, the ✕ — is identical, so it is built once here rather than duplicated per tool.
	// Figma nodes 19019:672041 "Toolbox hover" / 19019:672074 "Active", traced in REQ-400 §9a: the
	// main header ("Header", y=0) and the tool-options row ("Header", y=44) are TWO separate frames,
	// not one swapping into the other. Earlier this early-returned a replacement header instead of
	// adding a sibling row — that was the bug (the sub-row covered the main header). A fourth tool
	// should add a caller, not a fourth copy.
	const renderToolOptionsBar = (
		icon: ReactNode,
		label: string,
		controls: ReactNode,
		// Every tool-options row gets one. The freehand design frame doesn't draw a ✕, but the shape
		// and note tools both have one and consistency across the three won out — a row the user can
		// only dismiss by knowing about Escape or the toggle button is a worse row.
		onClose?: () => void,
		// The note row keeps its own BEM block (`wv-note-toolbar`) so its rules can diverge from the
		// shape/pen row's later; public/webviewer-quotation.css lists both selectors side by side today.
		variant: "shape" | "note" = "shape",
		closeCopy: { ariaLabel: string; title: string } = { ariaLabel: "Done drawing highlights", title: "Done" },
	) => (
		<div className={`wv-header wv-${variant}-toolbar`}>
			<div className="wv-header__group">
				<span className={`wv-${variant}-toolbar__label`}>
					{icon}
					{label}
				</span>
				<div className="wv-toolbar-divider" />
				{controls}
			</div>
			{onClose ? (
				<div className="wv-header__group">
					<button className="wv-btn-close" onClick={onClose} aria-label={closeCopy.ariaLabel} title={closeCopy.title}>
						<CloseIcon />
					</button>
				</div>
			) : null}
		</div>
	)

	// The shape tool's five controls (node 19019:672061) — Color, Fill color, Opacity, Stroke width,
	// Line style (#1381 review: "the design has much more functionality than implemented"). Every
	// field is independently settable on the backend (verified via `pnpm run api:show`).
	const shapeToolbarRow = activeShapeTool
		? renderToolOptionsBar(
				activeShapeTool === "rectangle" ? <RectangleShapeIcon /> : <EllipseShapeIcon />,
				activeShapeTool === "rectangle" ? "Rectangle" : "Ellipse",
				<>
					{/* Outline and fill are independently settable on the backend (`color` / `fill_color`
					    are separate fields) — two pickers, matching the Figma mock's "Color" / "Fill color"
					    buttons (node 19019:672061). The two are told apart by their PREVIEW (#1386 review:
					    "it is not clear which color circle is responsible for what"): the stroke is a
					    hollow ring, the fill a solid disc, exactly as the design draws them. Only the fill
					    offers "no fill" — an outline with no colour would be nothing at all. */}
					<ColorSwatchPicker
						value={selectedStrokeColor}
						// `allowNoFill` is off here, so the picker can never emit null — the guard is what
						// keeps the store's non-nullable stroke honest without a cast.
						onChange={(color) => {
							if (color !== null) setSelectedStrokeColor(color)
						}}
						label="Color"
						previewVariant="ring"
					/>
					<ColorSwatchPicker value={selectedFillColor} onChange={setSelectedFillColor} label="Fill color" allowNoFill />
					<SliderPopover
						value={selectedOpacity}
						onChange={setSelectedOpacity}
						min={MIN_HIGHLIGHT_OPACITY}
						max={MAX_HIGHLIGHT_OPACITY}
						unit="%"
						icon={<OpacityIcon />}
						label="Opacity"
					/>
					<SliderPopover
						value={selectedStrokeWidth}
						onChange={setSelectedStrokeWidth}
						min={MIN_HIGHLIGHT_STROKE_WIDTH}
						max={MAX_HIGHLIGHT_STROKE_WIDTH}
						unit=" pt"
						icon={<StrokeWidthIcon />}
						label="Stroke width"
						showNumberInput
					/>
					{/* Cloudy is offered only for a shape Apryse can actually draw a cloud for — on this
					    build that is the rectangle and not the ellipse, so the row is hidden rather than
					    saved-then-drawn-solid. Feature-probed, not hardcoded per shape, so a future SDK
					    that implements it for ellipses starts offering it with no code change here.
					    `useFigureHighlightCreate.armShapeTool` handles the other half: resetting the
					    selection when the user switches from a shape that supports it to one that
					    doesn't. */}
					<LineStylePicker
						value={selectedLineStyle}
						onChange={setSelectedLineStyle}
						hiddenValues={cloudyUnsupportedStyles}
					/>
				</>,
				onDisarmShapeTool,
			)
		: null

	// REQ-400 #1324 — the freehand tool's three controls (node 18916:553416): Color, Opacity,
	// Thickness. Not the shape row's five: an Ink annotation has no fill, and the freehand endpoint
	// accepts neither `fill_color` nor `line_style`, so those two pickers would be offering settings
	// that cannot be sent. It DOES get the same ✕ as the shape and note rows, which the design frame
	// omits — consistency across the three tools won out.
	const freehandToolbarRow = isFreehandArmed
		? renderToolOptionsBar(
				<HighlightToolIcon />,
				"Pen",
				<>
					<ColorSwatchPicker
						value={freehandColor}
						// No `allowNoFill` — a pen stroke with no colour is nothing at all — so null never
						// arrives; same guard as the shape toolbar's stroke picker and the note row's.
						onChange={(color) => {
							if (color !== null) setFreehandColor(color)
						}}
						label="Color"
					/>
					<SliderPopover
						value={freehandOpacity}
						onChange={setFreehandOpacity}
						min={MIN_HIGHLIGHT_OPACITY}
						max={MAX_HIGHLIGHT_OPACITY}
						unit="%"
						icon={<OpacityIcon />}
						label="Opacity"
					/>
					<SliderPopover
						value={freehandStrokeWidth}
						onChange={setFreehandStrokeWidth}
						min={MIN_FREEHAND_HIGHLIGHT_STROKE_WIDTH}
						max={MAX_FREEHAND_HIGHLIGHT_STROKE_WIDTH}
						unit=" pt"
						icon={<StrokeWidthIcon />}
						// "Thickness" here, "Stroke width" on the shape row — the design renames the same
						// control for the highlighter (node 18916:553604 is "Highlight / Thickness / Opened").
						label="Thickness"
						showNumberInput
					/>
				</>,
				onToggleFreehandTool,
			)
		: null

	// REQ-395/396 — the note tool's two controls: font size and text color. Figma shows nothing else
	// for a note, and the create endpoint accepts nothing else either.
	const noteToolbarRow = isNoteToolbarOpen
		? renderToolOptionsBar(
				<NoteToolIcon />,
				"Text",
				<>
					<FontSizePicker
						value={selectedNoteFontSize}
						onChange={setSelectedNoteFontSize}
						options={NOTE_FONT_SIZE_OPTIONS}
					/>
					<ColorSwatchPicker
						value={selectedNoteTextColor}
						// No `allowNoFill` here either — text with no colour is invisible — so null never
						// arrives; same guard as the shape toolbar's stroke picker.
						onChange={(color) => {
							if (color !== null) setSelectedNoteTextColor(color)
						}}
						colors={NOTE_TEXT_COLOR_PALETTE}
						label="Text color"
					/>
				</>,
				onCloseNoteToolbar,
				"note",
				{ ariaLabel: "Close note options", title: "Close" },
			)
		: null

	return (
		<div className="wv-header-stack">
			<div className="wv-header">
				<div className="wv-header__group">
					<div className="wv-header__tool-pair">
						<Dropdown
							items={menuItems}
							placement="bottomLeft"
							overlayStyle={{ zIndex: 1500 }}
							open={isDropdownOpen}
							onOpenChange={setIsDropdownOpen}
						>
							<span>
								<ButtonBurger className="wv-btn-tools" />
							</span>
						</Dropdown>
						<Dropdown
							items={viewControlItems}
							placement="bottomLeft"
							overlayClassName="wv-header-menu"
							overlayStyle={{ zIndex: 1500, width: 224 }}
							open={isViewControlsOpen && !isLayoutMenuDisabled}
							// Guarded rather than relying on the disabled button alone: antd's default trigger is
							// hover, and the Dropdown wrapper — not the button — is what receives it.
							onOpenChange={(open) => {
								if (!isLayoutMenuDisabled) setIsViewControlsOpen(open)
							}}
						>
							<button
								className={`wv-btn-tools wv-btn-icon${isViewControlsOpen && !isLayoutMenuDisabled ? " wv-btn-icon--open" : ""}`}
								onClick={() => setIsViewControlsOpen(!isViewControlsOpen)}
								disabled={isLayoutMenuDisabled}
								aria-label="Page layout"
							>
								<ControllICon />
							</button>
						</Dropdown>
						{/* Dual-view control — slot 3 of the left group, directly right of the layout dropdown
						    (#1311 AC3). It hosts the dual-view menu in EVERY state since #1430: the menu's
						    first two rows are "a second view here" and "a second view in its own window", so
						    the control can no longer act on a click without choosing between them for the
						    user. Both of those rows, and the Close rows below them, still go through the same
						    guarded `setSplit` / `usePopOutWindow` entry points. */}
						{isSplitFeatureEnabled ? (
							<div className="wv-split-control">
								{isDualViewMenuUsable ? (
									<Dropdown
										items={splitViewItems}
										placement="bottomLeft"
										overlayClassName="wv-header-menu"
										// Sized to the LONGEST row plus its keyboard digit. That row used to be "Auto open
										// floor plan" (237px measured, hence the inherited 240); #1430 adds the longer
										// "Return right view back". The digits are pinned to a common right edge (see
										// .wv-header-menu in webviewer-quotation.css), so a row that outgrows the box
										// ELLIPSIZES rather than pushing its digit out — which is exactly what browser
										// verification caught at 248 ("Return right view b…"), hence 264. Re-check this
										// in the browser whenever a label is added; the failure is silent and cosmetic.
										overlayStyle={{ zIndex: 1500, width: 264 }}
										open={isSplitMenuOpen}
										// A row's own click is ignored here because the row handler owns that decision:
										// it calls closeMenu itself, so the digit path and the click path share one rule
										// (see splitViewItems). Everything else — an outside click, the trigger — closes.
										onOpenChange={(open, info) => {
											if (info.source === "menu") return
											if (open) setOpenMenu("splitView")
											else closeMenu("splitView")
										}}
									>
										{/* The button IS the trigger — no separate caret (design review). */}
										<div className="wv-split-control__trigger">{splitViewButton}</div>
									</Dropdown>
								) : (
									// Nothing the menu offers can act (a single-page document with the pop-out
									// unavailable too), so there is no menu to host and the button is inert — which is
									// exactly when "why can I not click this?" needs answering. The tooltip lives only
									// here: in every other state the same hover opens the menu, and a bottom-placed
									// tooltip would land straight on top of it (#1383 review).
									<Tooltip
										title="Dual view (D)"
										placement="bottom"
										getPopupContainer={getShadowTooltipContainer}
										styles={compactTooltipStyles}
									>
										{/* A disabled button fires no pointer events, so antd needs a live wrapper to hang
										    the tooltip on — otherwise it silently stops working on a single-page document,
										    which is the one state that reaches this branch. */}
										<span className="wv-split-control__trigger">{splitViewButton}</span>
									</Tooltip>
								)}
							</div>
						) : null}
					</div>
					<div className="wv-toolbar-divider" />
					<ZoomControls
						active={activeTool === "zoom"}
						activeDirection={quotationZoomDirection}
						zoomPercent={zoomPercent}
						onZoomIn={() => onToolChange("zoom", "in")}
						onZoomOut={() => onToolChange("zoom", "out")}
						onZoomInStep={() => zoomByStep(false)}
						onZoomOutStep={() => zoomByStep(true)}
						onZoomScaleChange={setZoomScale}
					/>
					<div className="wv-toolbar-divider" />
					<div className="wv-viewer-tool-pair">
						<Tooltip
							title="Pan (Shift + V)"
							placement="bottom"
							getPopupContainer={getShadowTooltipContainer}
							styles={compactTooltipStyles}
						>
							<button
								className={`wv-btn-tools wv-btn-viewer-tool${activeTool === "pan" ? " wv-btn-viewer-tool--active" : ""}`}
								onClick={() => onToolChange("pan")}
								aria-label="Pan tool"
							>
								<PanToolIcon active={activeTool === "pan"} />
							</button>
						</Tooltip>
						<Tooltip
							title="Select (V)"
							placement="bottom"
							getPopupContainer={getShadowTooltipContainer}
							styles={compactTooltipStyles}
						>
							<button
								// `syncFromTool` only tracks pan/zoom/measure/edit, so with the Highlight or Shapes
								// tool armed the Apryse mode is one it doesn't recognise and `activeTool` keeps its
								// previous value — usually "select". Without this, two buttons read as pressed at
								// once. The armed highlight tool owns the canvas, so it owns the pressed state.
								className={`wv-btn-tools wv-btn-viewer-tool${
									activeTool === "select" && !isFreehandArmed && !activeShapeTool ? " wv-btn-viewer-tool--active" : ""
								}`}
								onClick={() => onToolChange("select")}
								aria-label="Select tool"
							>
								<SelectToolIcon active={activeTool === "select"} />
							</button>
						</Tooltip>
					</div>
					{onToggleNoteTool ? (
						<>
							<div className="wv-toolbar-divider" />
							{/* Shadow-DOM-aware Tooltip, matching the Pen button beside it — a native `title`
							    renders as the browser's own tooltip, which looks nothing like the rest of
							    the toolbar's. */}
							<Tooltip
								title="Text (T)"
								placement="bottom"
								getPopupContainer={getShadowTooltipContainer}
								styles={compactTooltipStyles}
							>
								<button
									className={`wv-btn-tools wv-btn-note${isNoteToolArmed ? " wv-btn-note--active" : ""}`}
									onClick={onToggleNoteTool}
									aria-pressed={isNoteToolArmed}
									aria-label="Text tool"
								>
									<NoteToolIcon />
								</button>
							</Tooltip>
						</>
					) : null}
					<div className="wv-toolbar-divider" />
					<Tooltip
						title="Pen (P)"
						placement="bottom"
						getPopupContainer={getShadowTooltipContainer}
						styles={compactTooltipStyles}
					>
						<button
							className={`wv-btn-tools wv-btn-viewer-tool${isFreehandArmed ? " wv-btn-viewer-tool--active" : ""}`}
							onClick={onToggleFreehandTool}
							aria-label="Pen tool"
							aria-pressed={isFreehandArmed}
							disabled={!onToggleFreehandTool}
						>
							<HighlightToolIcon />
						</button>
					</Tooltip>
					<div className="wv-toolbar-divider" />
					<Dropdown
						items={shapeMenuItems}
						placement="bottomLeft"
						overlayStyle={{ zIndex: 1500 }}
						open={openHeaderMenu === "shapes"}
						onOpenChange={(open) => (open ? setOpenMenu("shapes") : closeMenu("shapes"))}
					>
						<button
							className={`wv-btn-tools wv-btn-shapes${activeShapeTool ? " wv-btn-shapes--active" : ""}`}
							onClick={() => toggleMenu("shapes")}
							aria-label="Shapes"
							title="Shapes (S)"
							aria-pressed={Boolean(activeShapeTool)}
						>
							<ShapesToolIcon />
							<img
								className={`wv-zoom-chevron-icon${openHeaderMenu === "shapes" ? " wv-zoom-chevron-icon--open" : ""}`}
								src={chevronDownIcon}
								alt=""
							/>
						</button>
					</Dropdown>
					<div className="wv-toolbar-divider" />
					<button
						className={`wv-btn-tools wv-btn-ruler${isRulerActive ? " wv-btn-ruler--active" : ""}`}
						onClick={onToggleRulerTool}
						title="Measurement / Ruler (M)"
					>
						<RulerIcon />
					</button>
					<div className="wv-toolbar-divider" />
					<Dropdown
						items={addMenuItems}
						placement="bottomLeft"
						overlayStyle={{ zIndex: 1500 }}
						open={openHeaderMenu === "add"}
						onOpenChange={(open) => (open ? setOpenMenu("add") : closeMenu("add"))}
					>
						<button className="wv-btn-tools wv-btn-add" onClick={() => toggleMenu("add")}>
							+ Add
						</button>
					</Dropdown>
				</div>

				<div className="wv-header__group">
					<Dropdown
						items={helpMenuItems}
						placement="bottomRight"
						overlayStyle={{ zIndex: 1500 }}
						open={openHeaderMenu === "help"}
						onOpenChange={(open) => (open ? setOpenMenu("help") : closeMenu("help"))}
					>
						<button
							className="wv-btn-tools wv-btn-icon wv-btn-help"
							onClick={() => toggleMenu("help")}
							aria-label="Help"
						>
							<HelpIcon />
						</button>
					</Dropdown>
					{/* <button className="wv-btn-ai-agent"><AIAgentViewerIcon /> AI Agent</button> */}
					{/* <button className="wv-btn-generate-price">Generate price</button> */}
					<Tooltip
						title="Quotation configuration (C)"
						placement="bottom"
						getPopupContainer={getShadowTooltipContainer}
						styles={compactTooltipStyles}
					>
						<button
							className="wv-btn-tools wv-btn-icon wv-btn-settings"
							onClick={onSettings}
							aria-label="Quotation configuration"
						>
							<SettingsIcon />
						</button>
					</Tooltip>
					<button className="wv-btn-close" onClick={onClose}>
						<CloseIcon />
					</button>
				</div>
			</div>
			{/* At most ONE tool-options row, ever — a single slot, deliberately not three siblings.
			    Each row is gated on its own store, and a stale flag on any of them (a selected note in
			    particular, which keeps its row up independently of the armed flag) used to stack two
			    bars in the header. The three modes disarm each other at every entry point, so this is
			    the belt rather than the mechanism — but it is also what keeps `getToggleExtraTopOffset`
			    honest, since that reports a SINGLE row's height. An armed drawing tool wins over the
			    note row: it is an explicit "about to draw" mode the user just picked. */}
			{shapeToolbarRow || freehandToolbarRow || noteToolbarRow}
		</div>
	)
}
